import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "panel"]
  static values = { active: String, syncInputSelector: String, delegate: String, openFromHash: Boolean }

  connect() {
    if (this.delegateValue) {
      this._boundDelegateClick = this.#handleDelegateClick.bind(this)
      const el = document.querySelector(this.delegateValue)
      if (el) el.addEventListener("click", this._boundDelegateClick, true)
    }

    this._boundKeydown = this.#handleKeydown.bind(this)
    this.buttonTargets.forEach((button) => button.addEventListener("keydown", this._boundKeydown))

    const defaultKey = this.#keyFromHash() || this.activeValue || this.buttonTargets[0]?.dataset.tabKey
    this.activateByKey(defaultKey)
  }

  disconnect() {
    if (this._boundDelegateClick && this.delegateValue) {
      const el = document.querySelector(this.delegateValue)
      if (el) el.removeEventListener("click", this._boundDelegateClick, true)
    }
    if (this._boundKeydown) {
      this.buttonTargets.forEach((button) => button.removeEventListener("keydown", this._boundKeydown))
    }
  }

  #handleKeydown(event) {
    const buttons = this.buttonTargets
    const currentIndex = buttons.indexOf(event.currentTarget)
    if (currentIndex === -1) return

    let nextIndex = null
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % buttons.length
        break
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = buttons.length - 1
        break
      case "Enter":
      case " ":
        event.preventDefault()
        this.activateByKey(event.currentTarget.dataset.tabKey)
        return
      default:
        return
    }

    event.preventDefault()
    const next = buttons[nextIndex]
    this.activateByKey(next.dataset.tabKey)
    next.focus()
  }

  activate(event) {
    event.preventDefault()
    this.activateByKey(event.currentTarget.dataset.tabKey)
  }

  activateByKey(key) {
    if (!key) return
    this.syncInputValue(key)
    this.#renderActive(key)
    this.loadPanelOnVisit(key)
  }

  loadPanelOnVisit(key) {
    const panel = this.panelTargets.find((target) => target.dataset.tabKey === key)
    if (!panel?.dataset.url) return

    turboStreamHandler.runRequest(panel.dataset.url)
    if (panel.dataset.loadOnce !== undefined) delete panel.dataset.url
  }

  syncInputValue(key) {
    if (!this.hasSyncInputSelectorValue) return

    const input = document.querySelector(this.syncInputSelectorValue)
    if (input) input.value = key
  }

  #handleDelegateClick(event) {
    const link = event.target.closest("[data-tab-panel]")
    if (!link) return

    const key = link.dataset.tabPanel
    if (!this.panelTargets.some((el) => el.dataset.tabKey === key)) return

    event.preventDefault()
    event.stopImmediatePropagation()
    this.activateByKey(key)
  }

  #keyFromHash() {
    if (!this.openFromHashValue) return null
    const hash = location.hash.slice(1)
    return hash && this.buttonTargets.some((b) => b.dataset.tabKey === hash) ? hash : null
  }

  #renderActive(key) {
    this.buttonTargets.forEach((button) => {
      const isActive = button.dataset.tabKey === key
      button.classList.toggle("active", isActive)
      button.setAttribute("aria-selected", String(isActive))
      button.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("tw-hidden", panel.dataset.tabKey !== key)
    })
  }
}
